import { useEffect, useState, useCallback } from "react";
import { Loader2, Copy, RefreshCw, Send, ShieldCheck, ShieldAlert, Star, Clock } from "lucide-react";
import { toast } from "sonner";
import { EmailLog, SmsPanel } from "./EmailLogParts";

const Stat = ({ label, value, tone, testid }) => (
  <div data-testid={testid} className="border border-white/8 p-4 bg-white/[0.015]">
    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{label}</div>
    <div className={`font-display text-2xl mt-2 ${tone || "text-white"}`}>{value}</div>
  </div>
);

const DomainCard = ({ domain, onRecheck, checking }) => {
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };
  if (!domain) return null;
  const ok = domain.verified;
  return (
    <div data-testid="email-domain-card" className={`border p-5 ${ok ? "border-[color:var(--bl-green)]/40 bg-[color:var(--bl-green)]/5" : "border-[color:var(--bl-gold)]/40 bg-[color:var(--bl-gold)]/5"}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          {ok ? <ShieldCheck size={18} className="text-[color:var(--bl-green)] mt-0.5" /> : <ShieldAlert size={18} className="text-[color:var(--bl-gold)] mt-0.5" />}
          <div>
            <div className={`font-mono text-[11px] tracking-[0.25em] uppercase ${ok ? "text-[color:var(--bl-green)]" : "text-[color:var(--bl-gold)]"}`} data-testid="email-domain-status">
              {domain.domain?.name || "No domain"} · {ok ? "Verified" : `DNS ${domain.domain?.status || "missing"}`}
            </div>
            <div className="text-white/70 text-sm mt-1">
              Sending as <span className="font-mono text-white">{domain.sender}</span>
              {domain.sandbox && !ok && " — sandbox: only the account owner can receive mail until DNS is verified."}
              {domain.sandbox && ok && " — domain verified; ask the developer to switch the sender to hello@regenbiolabz.com."}
            </div>
          </div>
        </div>
        <button onClick={onRecheck} disabled={checking} data-testid="email-domain-recheck"
          className="bl-btn text-[10px] py-2 px-3 inline-flex items-center gap-2">
          {checking ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-check DNS
        </button>
      </div>
      {!ok && domain.domain?.records?.length > 0 && (
        <div className="mt-5 border border-white/8 divide-y divide-white/8">
          <div className="hidden md:grid grid-cols-[70px,200px,1fr,80px] gap-3 px-3 py-2 bg-white/[0.02] font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">
            <div>Type</div><div>Name</div><div>Value</div><div className="text-right">Status</div>
          </div>
          {domain.domain.records.map((r) => (
            <div key={r.name + r.type} data-testid={`dns-record-${r.name}`} className="grid md:grid-cols-[70px,200px,1fr,80px] gap-2 md:gap-3 px-3 py-3 items-center">
              <div className="font-mono text-xs text-[color:var(--bl-blue)]">{r.type}</div>
              <button onClick={() => copy(r.name)} className="font-mono text-xs text-white text-left inline-flex items-center gap-1.5 hover:text-[color:var(--bl-gold)]">
                {r.name} <Copy size={10} className="opacity-50" />
              </button>
              <button onClick={() => copy(r.value)} className="font-mono text-[11px] text-white/70 text-left truncate inline-flex items-center gap-1.5 hover:text-[color:var(--bl-gold)] min-w-0">
                <span className="truncate">{r.value}</span> <Copy size={10} className="opacity-50 shrink-0" />
              </button>
              <div className={`font-mono text-[10px] tracking-[0.2em] uppercase md:text-right ${r.status === "verified" ? "text-[color:var(--bl-green)]" : "text-red-300"}`}>{r.status}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReviewRequestsPanel = ({ admin }) => {
  const [info, setInfo] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => admin.get(`/admin/review-requests`).then(r => setInfo(r.data)).catch(() => setInfo(null)), [admin]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setRunning(true);
    try {
      const r = await admin.post(`/admin/review-requests/run`, {});
      toast.success(r.data.due ? `Sent ${r.data.sent}, failed ${r.data.failed}` : "Nothing due yet");
      await load();
    } catch { toast.error("Run failed"); }
    finally { setRunning(false); }
  };
  return (
    <div className="border border-white/8 p-5" data-testid="review-requests-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Star size={16} className="text-[color:var(--bl-gold)]" />
          <div>
            <div className="bl-eyebrow text-[color:var(--bl-gold)]">Review requests</div>
            <div className="text-white/70 text-sm mt-1" data-testid="review-requests-status">
              Buyers are emailed {info?.delay_days ?? 7} days after tracking is added · runs hourly ·{" "}
              <span className="text-white">{info?.due ?? "—"} due now</span> · {info?.recent?.length ?? 0} sent so far
            </div>
          </div>
        </div>
        <button onClick={run} disabled={running} data-testid="review-requests-run" className="bl-btn text-[10px] py-2 px-3 inline-flex items-center gap-2">
          {running ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send due now
        </button>
      </div>
    </div>
  );
};

const PaymentRemindersPanel = ({ admin }) => {
  const [info, setInfo] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => admin.get(`/admin/payment-reminders`).then(r => setInfo(r.data)).catch(() => setInfo(null)), [admin]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setRunning(true);
    try {
      const r = await admin.post(`/admin/payment-reminders/run`, {});
      toast.success(r.data.due ? `Sent ${r.data.sent}, failed ${r.data.failed}` : "No unpaid orders past 48h");
      await load();
    } catch { toast.error("Run failed"); }
    finally { setRunning(false); }
  };
  return (
    <div className="border border-white/8 p-5" data-testid="payment-reminders-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Clock size={16} className="text-[color:var(--bl-gold)]" />
          <div>
            <div className="bl-eyebrow text-[color:var(--bl-gold)]">Payment reminders</div>
            <div className="text-white/70 text-sm mt-1" data-testid="payment-reminders-status">
              Unpaid crypto-invoice orders get one gentle nudge after {info?.delay_hours ?? 48}h · runs hourly ·{" "}
              <span className="text-white">{info?.due ?? "—"} due now</span> · {info?.recent?.length ?? 0} sent so far
            </div>
          </div>
        </div>
        <button onClick={run} disabled={running} data-testid="payment-reminders-run" className="bl-btn text-[10px] py-2 px-3 inline-flex items-center gap-2">
          {running ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send due now
        </button>
      </div>
    </div>
  );
};

export default function AdminEmails({ admin, onUnauthed }) {
  const [data, setData] = useState(null);
  const [domain, setDomain] = useState(null);
  const [sms, setSms] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async (f = filter) => {
    setLoading(true);
    try {
      const q = f === "all" ? "" : `?status=${f}`;
      const [e, s] = await Promise.all([admin.get(`/admin/email-log${q}`), admin.get(`/admin/sms-log`)]);
      setData(e.data); setSms(s.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthed();
      else toast.error("Couldn't load email log");
    } finally { setLoading(false); }
  }, [admin, onUnauthed, filter]);

  useEffect(() => {
    load(filter);
    admin.get(`/admin/email-domain`).then(r => setDomain(r.data)).catch(() => setDomain(null));
  }, [filter, load, admin]);

  const recheck = async () => {
    setChecking(true);
    try {
      const r = await admin.post(`/admin/email-domain/verify`, {});
      setDomain(d => ({ ...(d || {}), verified: r.data.verified, domain: r.data.domain }));
      toast[r.data.verified ? "success" : "message"](r.data.verified ? "Domain verified" : "Still waiting on DNS — records not found yet");
    } catch { toast.error("Could not reach Resend"); }
    finally { setChecking(false); }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testTo.includes("@")) { toast.error("Enter an email"); return; }
    setSending(true);
    try {
      const r = await admin.post(`/admin/email-test`, { to: testTo.trim() });
      toast[r.data.sent ? "success" : "error"](r.data.sent ? "Test email sent" : `Failed: ${r.data.reason || "unknown"}`);
      setTestTo("");
      await load(filter);
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const counts = data?.counts || { sent: 0, failed: 0, skipped: 0 };

  return (
    <div className="space-y-6" data-testid="admin-emails-panel">
      <DomainCard domain={domain} onRecheck={recheck} checking={checking} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="email-stat-total" label="Emails Total" value={data?.total ?? "—"} />
        <Stat testid="email-stat-sent" label="Delivered" value={counts.sent} tone="text-[color:var(--bl-green)]" />
        <Stat testid="email-stat-failed" label="Failed" value={counts.failed} tone={counts.failed ? "text-red-300" : "text-white"} />
        <Stat testid="email-stat-sms" label="SMS Sent" value={sms ? sms.log.filter(x => x.status === "sent").length : "—"}
          tone={sms?.configured ? "text-[color:var(--bl-blue)]" : "text-white/40"} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5">
          {["all", "sent", "failed", "skipped"].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`email-filter-${f}`}
              className={`px-3 py-2 font-mono text-[10px] tracking-[0.25em] uppercase border ${
                filter === f ? "border-[color:var(--bl-gold)] text-[color:var(--bl-gold)]" : "border-white/15 text-white/60 hover:text-white"}`}>{f}</button>
          ))}
        </div>
        <form onSubmit={sendTest} className="flex gap-2">
          <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="Send a test to…" type="email"
            data-testid="email-test-to"
            className="bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-3 py-2 text-white font-mono text-xs w-56" />
          <button type="submit" disabled={sending} data-testid="email-test-send"
            className="bl-btn gold text-[10px] py-2 px-3 inline-flex items-center gap-2">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Test
          </button>
        </form>
      </div>

      <EmailLog loading={loading} log={data?.log} filter={filter} />

      <PaymentRemindersPanel admin={admin} />
      <ReviewRequestsPanel admin={admin} />

      <SmsPanel sms={sms} />
    </div>
  );
}
